import React from 'react';
import { useQueryClient } from 'react-query';
import { DataReconciliationTable } from './DataReconciliationTable/DataReconciliationTable';
import { usePlaidLink } from '../_hooks/plaid/usePlaidLink';
import Spinner from '../CommonComponents/Spinner';
import { DataReconciliationTableLoading } from './DataReconciliationTable/DataReconciliationTable.loading';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';
import { useDataReconciliation } from './useDataReconciliation';
import { HELD_AWAY_INSTITUTIONS_QUERY_KEY } from './api/useDataReconciliationQuery';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';

interface Props {
  clientId: string;
}

export const DataReconciliation: React.FC<Props> = ({ clientId }) => {
  const queryClient = useQueryClient();

  const {
    open: openPlaid,
    isLoading: isOpeningPlaid,
    queryError: openPlaidError,
    mutationError: upsertLinkError,
  } = usePlaidLink({
    clientId,
    isDataReconciliation: true,
    onSuccess: () => {
      return queryClient.invalidateQueries(HELD_AWAY_INSTITUTIONS_QUERY_KEY);
    },
  });

  const {
    isLoading: isLoadingDataReconciliation,
    error: dataReconciliationError,
    data,
  } = useDataReconciliation({
    clientId,
  });

  useDocumentTitle('Reconciled Held-Away Accounts');

  if (dataReconciliationError) {
    return <Alert message={dataReconciliationError.toString()} variant="error" />;
  }

  return (
    <Box display="flex" flexDirection="column" width="100%" minWidth={1000} pr={30}>
      {isOpeningPlaid && <Spinner />}

      <Box display="flex" alignItems="center" width="100%" mb="32">
        <Box display="flex" flexDirection="column" flex={1}>
          <Text as="h1" fontSize="h5" fontWeight={500} mb="8">
            Reconciled Held-Away Accounts
          </Text>
          <Text fontSize="small" color="neutral.500" maxWidth={588}>
            Please add any accounts for which you would like full reconciliation. Investment
            accounts added for reconciliation will have access to the investment dashboard. All
            accounts added here will be pending review from your advisor before they are visible in
            your profile.
          </Text>
        </Box>

        <Button variant="primary" onClick={() => openPlaid()} disabled={isOpeningPlaid}>
          Add Institution
        </Button>
      </Box>

      {openPlaidError && <Alert message={openPlaidError.toString()} variant="error" mb="16" />}
      {upsertLinkError && <Alert message={upsertLinkError.toString()} variant="error" mb="16" />}

      {!isLoadingDataReconciliation &&
        data?.brokenLinks.map(({ institutionName, itemId }) => (
          <Alert
            key={itemId}
            title={`${institutionName} Disconnected`}
            variant="warning"
            extraAction={{
              text: 'Fix Connection',
              action: () => openPlaid(itemId),
            }}
            mb="16"
          />
        ))}

      {isLoadingDataReconciliation ? (
        <DataReconciliationTableLoading />
      ) : (
        data?.institutions && (
          <DataReconciliationTable institutions={data.institutions} clientId={clientId} />
        )
      )}
    </Box>
  );
};
